import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stream } from "@cloudflare/stream-react";

function VideoGrid({ title, description, videos }) {
  return (
    <div className="gallery-page">
      <h1>{title}</h1>
      <p>{description}</p>
      {videos.length === 0 ? (
        <div className="no-media"><p>No videos found.</p></div>
      ) : (
        <div className="videos-grid">
          {videos.map((video) => (
            <article key={video.url} className="video-card">
              <Stream
                controls
                src={video.playbackId}
                responsive
                preload="metadata"
                autoplay={false}
                muted={false}
                loop={false}
                primaryColor="#d9a949"
                letterboxColor="#000000"
              />
              <div className="video-info">
                <span className="filename">{video.title || video.filename}</span>
                <span className="size">Cloudflare Stream</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function useVideoCollection(endpoint) {
  const [state, setState] = useState({ loading: true, locked: false, error: "", videos: [] });

  const load = async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await fetch(endpoint, { headers: { Accept: "application/json" }, cache: "no-store" });
      if (response.status === 401) {
        setState({ loading: false, locked: true, error: "", videos: [] });
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setState({ loading: false, locked: false, error: "", videos: Array.isArray(data.videos) ? data.videos : [] });
    } catch (error) {
      console.error("Error loading videos:", error);
      setState({ loading: false, locked: false, error: "The video collection could not be loaded.", videos: [] });
    }
  };

  useEffect(() => { load(); }, [endpoint]);
  return { ...state, reload: load };
}

export function PublicVideos() {
  const { loading, error, videos } = useVideoCollection("/media/index.json");
  const [activeTab, setActiveTab] = useState("by");
  if (loading) return <div className="loading">Loading public videos…</div>;
  if (error) return <div className="error">{error}</div>;

  const tabs = [
    { id: "about", label: "About Abouna", description: "Videos about Father Paul and his life." },
    { id: "by", label: "By Abouna", description: "Videos featuring Father Paul’s own words and ministry." },
    { id: "for", label: "For Abouna", description: "Tributes and messages created for Father Paul." },
  ];
  const active = tabs.find((tab) => tab.id === activeTab) || tabs[1];
  const visibleVideos = videos.filter((video) => (video.category || "by") === active.id);

  return (
    <section className="gallery-page public-video-gallery">
      <h1>Public Videos</h1>
      <p>Explore videos about Father Paul, by Father Paul, and created in his honor.</p>
      <div className="video-tabs" role="tablist" aria-label="Public video categories">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`video-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`video-panel-${tab.id}`}
            className={activeTab === tab.id ? "video-tab is-active" : "video-tab"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        id={`video-panel-${active.id}`}
        role="tabpanel"
        aria-labelledby={`video-tab-${active.id}`}
        className="video-tab-panel"
      >
        <VideoGrid title={active.label} description={active.description} videos={visibleVideos} />
      </div>
    </section>
  );
}

export function PrivateVideos() {
  const { loading, locked, error, videos, reload } = useVideoCollection("/api/private-videos");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const unlock = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setPasswordError("");
    try {
      const response = await fetch("/api/private-about/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        setPasswordError("That password is not correct. Please try again.");
        return;
      }
      setPassword("");
      await reload();
    } catch {
      setPasswordError("The private videos could not be unlocked. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading private videos…</div>;
  if (error) return <div className="error">{error}</div>;
  if (locked) {
    return (
      <section className="about-lock" role="main" aria-labelledby="private-videos-title">
        <div className="about-lock__card">
          <div className="about-lock__cross" aria-hidden="true">🔒</div>
          <h1 id="private-videos-title">Private Videos</h1>
          <p>Enter the family password to view the private videos.</p>
          <form onSubmit={unlock} className="about-lock__form">
            <label htmlFor="private-video-password">Enter password to continue</label>
            <input
              id="private-video-password"
              type="password"
              value={password}
              onChange={(event) => { setPassword(event.target.value); setPasswordError(""); }}
              autoComplete="off"
              autoFocus
            />
            {passwordError && <p className="about-lock__error" role="alert">{passwordError}</p>}
            <button type="submit" disabled={submitting}>{submitting ? "Checking…" : "View private videos"}</button>
          </form>
        </div>
      </section>
    );
  }

  return <VideoGrid title="Private Videos" description="Password-protected family videos." videos={videos} />;
}

export default function GalleryVideos() {
  const navigate = useNavigate();
  return (
    <div className="gallery-page">
      <h1>Videos Gallery</h1>
      <p>Choose a video collection.</p>
      <div className="video-library-choices">
        <button type="button" className="video-library-card" onClick={() => navigate("/gallery/videos/public")}>
          <span className="video-library-card__icon" aria-hidden="true">🌐</span>
          <strong>Public Videos</strong>
          <span>Open videos available to everyone</span>
        </button>
        <button type="button" className="video-library-card" onClick={() => navigate("/gallery/videos/private")}>
          <span className="video-library-card__icon" aria-hidden="true">🔒</span>
          <strong>Private Videos</strong>
          <span>Password-protected family collection</span>
        </button>
      </div>
    </div>
  );
}
